import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    IFolderRole,
    IRoleSource,
    IUserRole,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { FoldersService } from '@services';

import {
    BehaviorSubject,
    merge,
    Subject,
} from 'rxjs';
import {
    finalize,
    takeUntil,
    tap,
} from 'rxjs/operators';

const ENTITIES_PER_REQUEST = 10;
const INITIAL_BATCH_SIZE = 30;

@Component({
    selector: 'ui-user-permissions',
    templateUrl: './user-permissions.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: [
        './user-permissions.scss',
    ],
    encapsulation: ViewEncapsulation.None,
})
export class UserPermissionsComponent implements OnInit, OnDestroy {
    @Input()
    public set username(value: string) {
        this._username = value;
        this._usernameChange$.next();

        this._loadInitialBatch();
    }

    @Input()
    public scrollBottom!: Subject<boolean>;

    public data$ = new BehaviorSubject<(IUserRole[] | IFolderRole)[]>([]);
    public loadingData$ = new BehaviorSubject(true);

    private _username!: string;
    private _hasMoreData = false;

    private _folderRoles: IFolderRole[] = [];

    private _destroyed$ = new Subject();
    private _usernameChange$ = new Subject();

    constructor(
        private _folders: FoldersService,
        private _translate: TranslateService,
    ) { }

    ngOnInit() {
        this.scrollBottom
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(this._loadMoreData);
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public formatRoleSources = (sources: IRoleSource[]) => {
        const groups = sources.reduce((acc, curr) => {
            if (curr.isInherited) {
                acc.inherited.push(curr.username);
            } else {
                acc.explicit.push(curr.username);
            }
            return acc;
        }, {
                explicit: [] as string[],
                inherited: [] as string[],
            });

        const formattedSources = [];

        if (groups.explicit.length) {
            formattedSources.push(this._translate.instant('check_perms_modal_explicit'));
        }

        if (groups.inherited.length) {
            formattedSources.push(
                this._translate.instant('check_perms_modal_inherited_ad_groups', {
                    groups: groups.inherited.join(', '),
                }),
            );
        }

        return formattedSources.join(', ');
    }

    private _loadInitialBatch = () => {
        this.loadingData$.next(true);

        this._folders.getAllRolesForUser({
            username: this._username,
            skip: 0,
            take: INITIAL_BATCH_SIZE,
        }).pipe(
            finalize(() => this.loadingData$.next(false)),
            tap(roles => {
                this._hasMoreData = roles.count > INITIAL_BATCH_SIZE;

                this.data$.next([
                    ...(!!roles.tenantRoles.length ? [roles.tenantRoles] : []),
                    ...roles.folderRoles,
                ]);

                this._folderRoles = roles.folderRoles;

            }),
            takeUntil(
                merge(this._destroyed$, this._usernameChange$),
            ),
        ).subscribe();
    }

    private _loadMoreData = () => {
        if (
            !this._hasMoreData ||
            this.loadingData$.value
        ) {
            return;
        }

        this.loadingData$.next(true);

        this._folders.getAllRolesForUser({
            username: this._username,
            skip: this._folderRoles.length,
            take: ENTITIES_PER_REQUEST,
        }).pipe(
            finalize(() => this.loadingData$.next(false)),
            takeUntil(
                merge(this._destroyed$, this._usernameChange$),
            ),
        ).subscribe(roles => {
            this._folderRoles = this._folderRoles.concat(roles.folderRoles);
            this._hasMoreData = roles.count > this._folderRoles.length;

            this.data$.next(
                this.data$.value.concat(roles.folderRoles),
            );
        });
    }
}
